import { useEffect } from 'react';

import { ActionDTO, ActionType, EntityRefUtil } from '@gearbelt/global';
import { formatDistanceToNow } from 'date-fns';
import { useInView } from 'react-intersection-observer';

import { initials } from '@/lib/utils';

import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { actionTypeLabel } from './action-type-label';

// Per-surface label override; `undefined` takes the shared `actionTypeLabel`.
export type ActivityLabelResolver = (action: ActionDTO) => string | undefined;

// Rows written in one transaction share a created_at (now() is
// transaction-stable), so the API tie-breaks on a random uuid. IssueCreated has
// no batch that can tie today; it's here so the rule reads as "creation rows".
const CREATION_TYPES = new Set<ActionType>([ActionType.WorkOrderCreated, ActionType.IssueCreated]);

function orderForDisplay(actions: ActionDTO[]): ActionDTO[] {
  return [...actions].sort((a, b) => {
    const byTime = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    if (byTime !== 0) return byTime;
    return Number(CREATION_TYPES.has(a.type)) - Number(CREATION_TYPES.has(b.type));
  });
}

// Paging props keep TanStack Query's own names so a caller can pass the query
// result straight through without aliasing.
export function ActivityTimeline({
  actions,
  resolveLabel,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
}: {
  actions: ActionDTO[];
  resolveLabel?: ActivityLabelResolver;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}) {
  const { ref: loadMoreRef, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage?.();
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // After the hooks, so the early return can't change hook order between renders.
  if (actions.length === 0) {
    return <div className="text-grey-5 py-2 text-sm">No activity yet.</div>;
  }

  return (
    <div className="flex flex-col">
      {orderForDisplay(actions).map((action) => (
        <div key={action.id} className="flex justify-between gap-3 p-2">
          <div className="flex items-start gap-2">
            <Avatar>
              {action.user && (
                <>
                  <AvatarImage src={action.user.avatarUrl ?? undefined} />
                  <AvatarFallback>{initials(EntityRefUtil.createUserFullNameRef(action.user))}</AvatarFallback>
                </>
              )}
            </Avatar>
            <div className="flex flex-col gap-1">
              <div className="text-sm font-bold">{action.user && <>{EntityRefUtil.createUserFullNameRef(action.user)}</>}</div>
              <div className="border-grey-5 text-grey-5 flex h-[25px] items-center border-l-[3px] pl-5 text-xs italic">
                {resolveLabel?.(action) ?? actionTypeLabel(action.type)}
              </div>
            </div>
          </div>
          <div className="text-grey-5 text-xs">{formatDistanceToNow(new Date(action.createdAt), { addSuffix: true })}</div>
        </div>
      ))}
      {/* Sentinel: scrolling it into view pulls the next page. */}
      {hasNextPage && <div ref={loadMoreRef} className="h-1" />}
      {isFetchingNextPage && <div className="text-grey-5 pt-1 text-xs italic">Loading earlier activity…</div>}
    </div>
  );
}
